import React from 'react';
import { Trash2, AlertCircle, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MessageCountsByStatus } from '@common/reducers/UserSelectors';
import { useTranslation } from 'react-i18next';

type MessageStatusKey = 'pending' | 'failed';

interface MessageStatusHeaderProps {
  messageCounts: MessageCountsByStatus;
  visibleStatuses: {
    pending: boolean;
    failed: boolean;
  };
  onToggleStatus: (status: 'pending' | 'failed') => void;
  onClearStatus: (status: 'pending' | 'failed') => void;
  onClearAll: () => void;
}

interface StatusBadgeProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count: number;
  color: string;
  status: MessageStatusKey;
  isVisible: boolean;
  onToggle: () => void;
  onClear: () => void;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  icon: Icon,
  label,
  count,
  color,
  status,
  isVisible,
  onToggle,
  onClear
}) => {
  const { t } = useTranslation('common');
  return (
    <div
      className={cn(
        "flex items-center gap-1.5 px-2 py-1.5 rounded border transition-all text-sm",
        isVisible
          ? "bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600"
          : "bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 opacity-60"
      )}
    >
      <div
        className="flex items-center gap-1.5 cursor-pointer"
        onClick={onToggle}
        title={`${isVisible ? t('messageStatusHeader.hide') : t('messageStatusHeader.show')} ${label.toLowerCase()}`}
      >
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <span
          className={cn(
            "w-1.5 h-1.5 rounded-full",
            isVisible ? "bg-green-500" : "bg-gray-400 dark:bg-gray-600"
          )}
        />
        <span className="font-semibold text-gray-800 dark:text-gray-200">{count}</span>
      </div>
      {count > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="ml-0.5 p-0.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
          title={`${t('messageStatusHeader.clearAll')} ${label.toLowerCase()}`}
        >
          <Trash2 className="w-3 h-3 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400" />
        </button>
      )}
    </div>
  );
};

export default function MessageStatusHeader({
  messageCounts,
  visibleStatuses,
  onToggleStatus,
  onClearStatus,
  onClearAll
}: MessageStatusHeaderProps) {
  const { t } = useTranslation('common');
  const totalMessages = messageCounts.total;
  const statusBadges: Array<{
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    count: number;
    color: string;
    status: MessageStatusKey;
  }> = [
      {
        icon: Clock,
        label: t('messageStatusHeader.pending'),
        count: messageCounts.pending,
        color: 'text-[#53bdeb] dark:text-[#53bdeb]',
        status: 'pending'
      },
      {
        icon: AlertCircle,
        label: t('messageStatusHeader.failed'),
        count: messageCounts.failed,
        color: 'text-red-500 dark:text-red-400',
        status: 'failed'
      }
    ];

  const visibleStatusBadges = statusBadges.filter(({ count }) => count > 0);
  const hasVisibleBadges = visibleStatusBadges.length > 0;
  // Clear-all only makes sense for statuses it can actually act on (pending/failed).
  // A chat that is entirely e.g. in-flight ("sending") still shows the total, but the
  // button is suppressed rather than offering an action that deletes nothing.
  const clearableCount = messageCounts.pending + messageCounts.failed;

  return (
    <div className="w-full bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-3 py-2">
      <div
        className={cn(
          'flex items-center justify-between gap-2',
          !hasVisibleBadges && 'justify-end'
        )}
      >
        {hasVisibleBadges && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{t('messageStatusHeader.filter')}:</span>
            {visibleStatusBadges.map(
              ({ icon, label, count, color, status }) => (
                <StatusBadge
                  key={status}
                  icon={icon}
                  label={label}
                  count={count}
                  color={color}
                  status={status}
                  isVisible={visibleStatuses[status]}
                  onToggle={() => onToggleStatus(status)}
                  onClear={() => onClearStatus(status)}
                />
              )
            )}
          </div>
        )}
        {totalMessages > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 dark:text-gray-300">
              {t('messageStatusHeader.total')}: <span className="font-semibold">{totalMessages}</span>
            </span>

            {clearableCount > 0 && (
              <button
                onClick={onClearAll}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-500 dark:bg-red-600 text-white rounded text-xs hover:bg-red-600 dark:hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
                {t('messageStatusHeader.clearAll')}
              </button>
            )}
          </div>
        )}



      </div>
    </div>
  );
}

